import { fetchCart } from "theme";

(function() {
  'use strict';

  // Guard cart mutations so follow-up cart events do not trigger duplicate add/remove requests.
  let gamificationGiftAddInProgress = false;
  let gamificationGiftRemoveInProgress = false;

  /**
   * Custom element controller for the cart gamification bar.
   *
   * It owns three responsibilities:
   * 1. Render and update the threshold progress/message UI.
   * 2. Keep the gift chooser modal in sync with the selected product/variant.
   * 3. Add, remove, and validate managed gift lines in the Shopify cart.
   */
  class GamificationBar extends HTMLElement {
    /**
     * Set up instance-level listeners, parsed config, runtime state, and helpers.
     *
     * No DOM listeners are attached here yet; that happens in `connectedCallback()`.
     */
    constructor() {
      super();

      this.onCartChangedListener = this.onCartChanged.bind(this);
      this.onDrawerRefreshedListener = this.onDrawerRefreshed.bind(this);
      this.onGiftChoiceChangedListener = this.onGiftChoiceChanged.bind(this);
      this.onGiftChoiceClickedListener = this.onGiftChoiceClicked.bind(this);
      this.onGiftSubmitListener = this.onGiftSubmit.bind(this);
      this.onGiftVariantAddedListener = this.onGiftVariantAdded.bind(this);
      this.cartDrawerHost = null;
      this.config = this.getConfig();
      this.state = {
        totalPrice: this.config.total_price || 0,
        hasManagedGift: this.config.gift?.has_managed_gift === true
      };
      this.currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
        style: "currency",
        currency: Shopify.currency.active
      });
    }

    /**
     * Attach all document and drawer listeners, then initialize the UI from the live cart.
     *
     * This runs every time the custom element enters the DOM.
     *
     * @returns {void}
     */
    connectedCallback() {
      document.addEventListener("cart:change", this.onCartChangedListener);
      document.addEventListener("change", this.onGiftChoiceChangedListener);
      document.addEventListener("click", this.onGiftChoiceClickedListener);
      document.addEventListener("submit", this.onGiftSubmitListener, true);
      document.addEventListener("variant:add", this.onGiftVariantAddedListener);

      this.cartDrawerHost = this.closest("cart-drawer");

      if (this.cartDrawerHost) {
        this.cartDrawerHost.addEventListener("cart-drawer:refreshed", this.onDrawerRefreshedListener);
      }

      this.updateGamificationBar();
      this.syncGiftChooser();
      this.initializeGiftState();
    }

    /**
     * Remove every listener that was attached in `connectedCallback()`.
     *
     * This prevents duplicate listeners when the cart drawer DOM is replaced.
     *
     * @returns {void}
     */
    disconnectedCallback() {
      document.removeEventListener("cart:change", this.onCartChangedListener);
      document.removeEventListener("change", this.onGiftChoiceChangedListener);
      document.removeEventListener("click", this.onGiftChoiceClickedListener);
      document.removeEventListener("submit", this.onGiftSubmitListener, true);
      document.removeEventListener("variant:add", this.onGiftVariantAddedListener);

      if (this.cartDrawerHost) {
        this.cartDrawerHost.removeEventListener("cart-drawer:refreshed", this.onDrawerRefreshedListener);
      }

      this.cartDrawerHost = null;
    }

    /**
     * Bootstrap the component from the live cart object.
     *
     * Flow:
     * 1. Read the current cart total.
     * 2. Remove stale managed gifts that no longer belong to the configured gift set.
     * 3. Re-sync button/modal state.
     * 4. Run the normal remove/add checks.
     *
     * @returns {Promise<void>}
     */
    async initializeGiftState() {
      const cart = await fetchCart;
      this.state.totalPrice = cart.total_price || 0;

      if (await this.maybeRemoveStaleGift(cart)) {
        return;
      }

      await this.syncManagedGiftState(cart);
      await this.maybeRemoveGift(cart);
      await this.maybeAutoAddGift(cart);
    }

    /**
     * Parse JSON from a child element inside the component.
     *
     * @param {string} selector - Selector for the `<script type="application/json">` element.
     * @param {any} fallback - Value returned when the element is missing or JSON parsing fails.
     * @returns {any}
     */
    parseJSON(selector, fallback) {
      const element = this.querySelector(selector);

      if (!element) {
        return fallback;
      }

      try {
        return JSON.parse(element.textContent.trim());
      } catch {
        return fallback;
      }
    }

    /**
     * Return the server-rendered gamification config payload.
     *
     * This is the single source of truth for thresholds, messages, modal id, and configured gift products.
     *
     * @returns {{
     *   modal_id: string,
     *   total_price: number,
     *   thresholds: { shipping: number, gift: number, first: number, second: number, first_type: string, second_type: string },
     *   messages: { before: string, after_first: string, after_second: string },
     *   gift: { product_count: number, requires_choice: boolean, has_managed_gift: boolean, products: Array<any> }
     * }}
     */
    getConfig() {
      return this.parseJSON("[data-gamification-config]", {
        modal_id: "",
        total_price: 0,
        thresholds: {
          shipping: 0,
          gift: 0,
          first: 0,
          second: 0,
          first_type: "",
          second_type: ""
        },
        messages: {
          before: "",
          after_first: "",
          after_second: ""
        },
        gift: {
          product_count: 0,
          requires_choice: false,
          has_managed_gift: false,
          products: []
        }
      });
    }

    /**
     * Resolve the chooser modal element for this bar instance.
     *
     * @returns {HTMLElement | null}
     */
    getGiftModal() {
      return document.getElementById(this.config.modal_id);
    }

    /**
     * Sync the `hasManagedGift` runtime state from the actual cart contents.
     *
     * After the state changes, the bar UI and chooser UI are re-rendered so button visibility
     * and submit guards always reflect the real cart, not stale server HTML.
     *
     * @param {object} [cart] - Optional cart payload from a `cart:change` event.
     * @returns {Promise<void>}
     */
    async syncManagedGiftState(cart) {
      cart ??= await fetchCart;
      this.state.hasManagedGift = this.getManagedGiftItems(cart).length > 0;
      this.updateGamificationBar();
      this.syncGiftChooser();
    }

    /**
     * Re-render the visible gamification bar state from `this.config` + `this.state`.
     *
     * It updates:
     * - progress width
     * - localized message text
     * - milestone icons
     * - chooser CTA visibility
     * - modal visibility when the gift is no longer eligible
     * - cart footer shipping message
     *
     * @returns {void}
     */
    updateGamificationBar() {
      const totalPrice = this.state.totalPrice;
      const thresholds = this.config.thresholds;
      const giftConfig = this.config.gift;
      const progressElement = this.querySelector(".gamification-bar__progress");
      const messageElement = this.querySelector(".gamification-bar__message");
      const firstMilestone = this.querySelector('[data-milestone="first"]');
      const secondMilestone = this.querySelector('[data-milestone="second"]');
      const giftActionsElement = this.querySelector(".gamification-bar__actions");
      const giftChoiceModal = this.getGiftModal();

      if (progressElement && thresholds.second > 0) {
        progressElement.style.width = `${Math.min(100, Math.max(0, totalPrice / thresholds.second * 100))}%`;
      }

      if (messageElement) {
        const remainingAmount = totalPrice >= thresholds.second ? 0 : totalPrice >= thresholds.first ? thresholds.second - totalPrice : thresholds.first - totalPrice;
        messageElement.innerHTML = this.getMessageTemplate().replace(
          /\{\{\s*remaining_amount\s*\}\}/g,
          this.formatRemainingAmount(remainingAmount)
        );
      }

      this.updateMilestone(firstMilestone, thresholds.first, thresholds.first_type);
      this.updateMilestone(secondMilestone, thresholds.second, thresholds.second_type);

      if (giftActionsElement) {
        giftActionsElement.hidden = !(
          giftConfig.requires_choice
          && giftConfig.product_count > 0
          && totalPrice >= thresholds.gift
          && !this.state.hasManagedGift
        );
      }

      if (giftChoiceModal?.open && (this.state.hasManagedGift || totalPrice < thresholds.gift)) {
        giftChoiceModal.hide();
      }

      this.updateFooterMessage(totalPrice);
    }

    /**
     * Update the free-shipping footer text in the cart drawer.
     *
     * @param {number} totalPrice - Current cart total in cents.
     * @returns {void}
     */
    updateFooterMessage(totalPrice) {
      const shippingTextDrawerFooter = document.getElementById("shipping-text-drawer-footer");

      if (!shippingTextDrawerFooter) {
        return;
      }

      if (totalPrice >= this.config.thresholds.shipping) {
        shippingTextDrawerFooter.innerHTML = shippingTextDrawerFooter.getAttribute("data-reached-message");
      } else {
        shippingTextDrawerFooter.innerHTML = shippingTextDrawerFooter.getAttribute("data-unreached-message");
      }
    }

    /**
     * Replace a milestone icon depending on whether its threshold has been reached.
     *
     * @param {HTMLElement | null} milestoneElement - DOM node for the milestone.
     * @param {number} threshold - Threshold in cents for this milestone.
     * @param {string} type - Either `gift` or `shipping`.
     * @returns {void}
     */
    updateMilestone(milestoneElement, threshold, type) {
      if (!milestoneElement) {
        return;
      }

      const iconState = this.state.totalPrice >= threshold ? "reached" : type === "gift" ? "gift" : "shipping";
      const iconTemplate = this.querySelector(`template[data-icon="${iconState}"]`);

      if (iconTemplate) {
        milestoneElement.innerHTML = iconTemplate.innerHTML;
      }
    }

    /**
     * Choose the correct message template for the current progress state.
     *
     * @returns {string}
     */
    getMessageTemplate() {
      if (this.state.totalPrice >= this.config.thresholds.second) {
        return this.config.messages.after_second || "";
      }

      if (this.state.totalPrice >= this.config.thresholds.first) {
        return this.config.messages.after_first || "";
      }

      return this.config.messages.before || "";
    }

    /**
     * Format a cents amount using the active Shopify currency formatter.
     *
     * The replacement at the end keeps the formatted value safe for string replacement in HTML templates.
     *
     * @param {number} amount - Amount in cents.
     * @returns {string}
     */
    formatRemainingAmount(amount) {
      return this.currencyFormatter.format(Math.max(0, amount) / 100).replace(/\$/g, "$$$$");
    }

    /**
     * Find a configured gift product by product id.
     *
     * @param {string | number} productId
     * @returns {any | undefined}
     */
    getGiftProductById(productId) {
      return (this.config.gift.products || []).find((giftProduct) => `${giftProduct.id}` === `${productId}`);
    }

    /**
     * Derive a readable option label from an option input and its paired label element.
     *
     * @param {HTMLInputElement} input
     * @returns {string}
     */
    getGiftOptionLabel(input) {
      const optionLabelElement = input.nextElementSibling;

      return optionLabelElement?.getAttribute("data-option-value")
        || optionLabelElement?.getAttribute("data-tooltip")
        || optionLabelElement?.textContent?.trim()
        || "";
    }

    /**
     * Read the currently checked option values for a given product card.
     *
     * The result is sorted by option position so it can be matched back to a Shopify variant.
     *
     * @param {HTMLElement} container
     * @returns {Array<{ position: number, value: string }>}
     */
    getSelectedGiftOptionValues(container) {
      return Array.from(container.querySelectorAll("input[data-option-position]:checked"))
        .sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position")))
        .map((input) => ({
          position: parseInt(input.getAttribute("data-option-position")),
          value: this.getGiftOptionLabel(input)
        }));
    }

    /**
     * Find the variant in a configured gift product that matches the selected option values.
     *
     * @param {any} productData
     * @param {Array<{ position: number, value: string }>} selectedOptionValues
     * @returns {any | undefined}
     */
    getMatchingGiftVariant(productData, selectedOptionValues) {
      return productData?.variants?.find((variant) => selectedOptionValues.every(({ position, value }) => `${variant[`option${position}`] || ""}` === value));
    }

    /**
     * Return the currently selected product card inside the chooser modal.
     *
     * Falls back to the first card if no radio is checked yet.
     *
     * @returns {HTMLElement | null}
     */
    getSelectedGiftChoiceItem() {
      const giftChoiceModal = this.getGiftModal();

      if (!giftChoiceModal) {
        return null;
      }

      return giftChoiceModal.querySelector("[data-gift-product-radio]:checked")?.closest("[data-gift-choice-item]")
        || giftChoiceModal.querySelector("[data-gift-choice-item]");
    }

    /**
     * Sync visual selection state inside the chooser modal.
     *
     * The modal displays several product cards but submits through one shared form, so this
     * method keeps the selected card, radio button, and shared form data aligned.
     *
     * @returns {void}
     */
    syncGiftChooser() {
      const giftChoiceModal = this.getGiftModal();

      if (!giftChoiceModal) {
        return;
      }

      const giftChoiceItems = Array.from(giftChoiceModal.querySelectorAll("[data-gift-choice-item]"));
      const selectedGiftChoiceItem = this.getSelectedGiftChoiceItem();

      giftChoiceItems.forEach((giftChoiceItem) => {
        const isSelected = giftChoiceItem === selectedGiftChoiceItem;
        giftChoiceItem.classList.toggle("is-selected", isSelected);

        const radioInput = giftChoiceItem.querySelector("[data-gift-product-radio]");

        if (radioInput) {
          radioInput.checked = isSelected;
        }
      });

      this.syncGiftChoiceItem(selectedGiftChoiceItem);
    }

    /**
     * Map the currently selected gift card into the shared submit form.
     *
     * This updates:
     * - hidden variant/product properties
     * - visible selected option labels
     * - compare-at price text
     * - submit button disabled state
     *
     * @param {HTMLElement | null} giftChoiceItem
     * @returns {void}
     */
    syncGiftChoiceItem(giftChoiceItem) {
      const giftChoiceModal = this.getGiftModal();
      const giftSubmitForm = giftChoiceModal?.querySelector("[data-gift-submit-form] form");
      const giftSubmitButton = giftChoiceModal?.querySelector("[data-gift-shared-submit]");

      if (!giftChoiceItem || !giftSubmitForm || !giftSubmitButton) {
        return;
      }

      const productData = this.getGiftProductById(giftChoiceItem.getAttribute("data-gift-product-id"));

      if (!productData) {
        return;
      }

      const selectedOptionValues = this.getSelectedGiftOptionValues(giftChoiceItem);
      const matchingVariant = productData.has_only_default_variant
        ? productData.variants.find((variant) => `${variant.id}` === `${productData.selected_variant_id}`) || productData.variants[0]
        : this.getMatchingGiftVariant(productData, selectedOptionValues);
      const variantIdInput = giftSubmitForm.querySelector("[data-gift-shared-variant-id]");
      const giftProductPropertyInput = giftSubmitForm.querySelector("[data-gift-shared-product-id]");
      const giftVariantPropertyInput = giftSubmitForm.querySelector("[data-gift-shared-variant-property]");
      const comparePriceElement = giftChoiceItem.querySelector("[data-gift-compare-price]");

      selectedOptionValues.forEach(({ position, value }) => {
        const selectedOptionElement = giftChoiceItem.querySelector(`[data-gift-selected-option="${position}"]`);

        if (selectedOptionElement) {
          selectedOptionElement.textContent = value;
        }
      });

      if (!matchingVariant) {
        giftSubmitButton.disabled = true;
        return;
      }

      if (variantIdInput) {
        variantIdInput.value = `${matchingVariant.id}`;
      }

      if (giftProductPropertyInput) {
        giftProductPropertyInput.value = `${productData.id}`;
      }

      if (giftVariantPropertyInput) {
        giftVariantPropertyInput.value = `${matchingVariant.id}`;
      }

      if (comparePriceElement) {
        comparePriceElement.textContent = this.currencyFormatter.format((matchingVariant.compare_at_price || matchingVariant.price || 0) / 100);
      }

      this.updateGiftChoiceImage(giftChoiceItem, productData, matchingVariant);
      giftSubmitButton.disabled = !matchingVariant.available || this.state.hasManagedGift || this.state.totalPrice < this.config.thresholds.gift;
    }

    /**
     * Update the visible product image in a gift card when the selected variant changes.
     *
     * If the matched variant has its own image it is used, otherwise the product image remains
     * as the fallback.
     *
     * @param {HTMLElement} giftChoiceItem
     * @param {any} productData
     * @param {any} matchingVariant
     * @returns {void}
     */
    updateGiftChoiceImage(giftChoiceItem, productData, matchingVariant) {
      const giftImageElement = giftChoiceItem.querySelector("[data-gift-image-wrapper] img");
      if (!giftImageElement) {
        return;
      }

      const imageSource = matchingVariant?.featured_image || productData.featured_image;
      const imageAlt = matchingVariant?.featured_image_alt || productData.featured_image_alt || productData.title || "";
      if (!imageSource) {
        return;
      }

      giftImageElement.src = imageSource;
      giftImageElement.srcset = `${imageSource} 360w`;
      giftImageElement.alt = imageAlt;
    }

    /**
     * Return the auto-addable gift variant id when exactly one simple gift product is configured.
     *
     * Auto-add is only supported for a single product with a single default variant.
     *
     * @returns {string}
     */
    getAutoAddGiftVariantId() {
      const giftProducts = this.config.gift.products || [];

      if (giftProducts.length !== 1) {
        return "";
      }

      const giftProduct = giftProducts[0];

      if (!giftProduct || !giftProduct.has_only_default_variant || !giftProduct.variants?.length) {
        return "";
      }

      const giftVariant = giftProduct.variants[0];
      return giftVariant?.available ? `${giftVariant.id}` : "";
    }

    /**
     * Build a stable sessionStorage key for the current simple auto-add gift cycle.
     *
     * The key changes when either the auto-added variant or the configured threshold changes,
     * which lets a new gift configuration behave like a fresh cycle automatically.
     *
     * @returns {string}
     */
    getAutoGiftDismissStorageKey() {
      const autoAddGiftVariantId = this.getAutoAddGiftVariantId();

      if (!autoAddGiftVariantId || this.config.gift.requires_choice) {
        return "";
      }

      return `gamification:auto-gift-dismissed:${autoAddGiftVariantId}:${this.config.thresholds.gift}`;
    }

    /**
     * Check whether the current simple auto-add gift was manually dismissed by the customer.
     *
     * @returns {boolean}
     */
    isAutoGiftDismissed() {
      const storageKey = this.getAutoGiftDismissStorageKey();

      if (!storageKey) {
        return false;
      }

      try {
        return window.sessionStorage.getItem(storageKey) === "true";
      } catch {
        return false;
      }
    }

    /**
     * Remember that the customer dismissed the current simple auto-add gift while still above
     * threshold, so it should not be auto-added again until the threshold cycle resets.
     *
     * @returns {void}
     */
    setAutoGiftDismissed() {
      const storageKey = this.getAutoGiftDismissStorageKey();

      if (!storageKey) {
        return;
      }

      try {
        window.sessionStorage.setItem(storageKey, "true");
      } catch {
      }
    }

    /**
     * Clear the dismissal state for the current simple auto-add gift cycle.
     *
     * This is called when the customer drops below threshold, allowing the gift to be auto-added
     * again next time the threshold is crossed.
     *
     * @returns {void}
     */
    clearAutoGiftDismissed() {
      const storageKey = this.getAutoGiftDismissStorageKey();

      if (!storageKey) {
        return;
      }

      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {
      }
    }

    /**
     * Update the dismissal state for the simple auto-add gift.
     *
     * Rules:
     * - If the cart drops below threshold, clear the dismissed flag.
     * - If the customer manually removes an auto-added gift while still above threshold,
     *   mark it as dismissed so it won't be re-added immediately.
     *
     * Automatic theme-driven removals are ignored by checking the remove-in-progress guard.
     *
     * @param {object} cart
     * @param {boolean} previouslyHadManagedGift
     * @returns {void}
     */
    syncAutoGiftDismissState(cart, previouslyHadManagedGift) {
      const autoAddGiftVariantId = this.getAutoAddGiftVariantId();

      if (!autoAddGiftVariantId || this.config.gift.requires_choice) {
        return;
      }

      const hasManagedGiftNow = this.getManagedGiftItems(cart).length > 0;

      if ((cart.total_price || 0) < this.config.thresholds.gift) {
        this.clearAutoGiftDismissed();
        return;
      }

      if (previouslyHadManagedGift && !hasManagedGiftNow && !gamificationGiftRemoveInProgress) {
        this.setAutoGiftDismissed();
      }
    }

    /**
     * Identify managed gift lines that no longer match the currently configured gift products.
     *
     * This handles the case where the merchant changed the configured gift set while customers
     * still have old managed gifts in their cart.
     *
     * @param {object} cart
     * @returns {Array<any>}
     */
    getStaleManagedGiftItems(cart) {
      const configuredProducts = this.config.gift.products || [];
      const configuredProductIds = new Set(configuredProducts.map((product) => `${product.id}`));
      const configuredVariantIds = new Set(
        configuredProducts.flatMap((product) => (product.variants || []).map((variant) => `${variant.id}`))
      );

      return this.getManagedGiftItems(cart).filter((item) => {
        const productId = `${item.product_id || item.properties?._gift_product_id || ""}`;
        const variantId = `${item.variant_id || item.id || item.properties?._gift_variant_id || ""}`;

        return !configuredProductIds.has(productId) && !configuredVariantIds.has(variantId);
      });
    }

    /**
     * Remove stale managed gifts before any normal add/remove logic runs.
     *
     * Returns `true` when a removal was triggered so the caller can stop and wait for the next
     * cart refresh event.
     *
     * @param {object} [cart]
     * @returns {Promise<boolean>}
     */
    async maybeRemoveStaleGift(cart) {
      cart ??= await fetchCart;

      const staleManagedGiftItems = this.getStaleManagedGiftItems(cart);

      if (staleManagedGiftItems.length === 0 || gamificationGiftRemoveInProgress) {
        return false;
      }

      await Promise.all(staleManagedGiftItems.map((giftItem) => this.removeGiftFromCart(giftItem.key)));
      return true;
    }

    /**
     * Auto-add the configured simple gift once the threshold is reached.
     *
     * The add is skipped when:
     * - no gift threshold exists
     * - the gift requires a chooser modal
     * - the threshold is not reached
     * - another cart mutation is running
     * - any managed gift already exists in the cart
     *
     * @param {object} [cart]
     * @returns {Promise<void>}
     */
    async maybeAutoAddGift(cart) {
      cart ??= await fetchCart;

      const autoAddGiftVariantId = this.getAutoAddGiftVariantId();

      if (
        !this.config.thresholds.gift
        || !autoAddGiftVariantId
        || this.config.gift.requires_choice
        || this.state.totalPrice < this.config.thresholds.gift
        || gamificationGiftAddInProgress
        || this.isAutoGiftDismissed()
        || this.getManagedGiftItems(cart).length > 0
      ) {
        return;
      }

      await this.addGiftToCart(autoAddGiftVariantId, this.config.thresholds.gift);
    }

    /**
     * Remove managed gifts when the cart total falls back below the gift threshold.
     *
     * @param {object} [cart]
     * @returns {Promise<void>}
     */
    async maybeRemoveGift(cart) {
      cart ??= await fetchCart;

      const managedGiftItems = this.getManagedGiftItems(cart);

      if (
        !this.config.thresholds.gift
        || this.state.totalPrice >= this.config.thresholds.gift
        || managedGiftItems.length === 0
        || gamificationGiftRemoveInProgress
      ) {
        return;
      }

      await Promise.all(managedGiftItems.map((giftItem) => this.removeGiftFromCart(giftItem.key)));
    }

    /**
     * Return all cart lines that were inserted by the gamification flow.
     *
     * @param {object} cart
     * @returns {Array<any>}
     */
    getManagedGiftItems(cart) {
      return (cart.items || []).filter((item) => item.properties?._gift_source === "gamification_bar");
    }

    /**
     * Add a managed gift variant to the cart.
     *
     * The request stamps custom properties onto the line item so the theme and the discount
     * function can reliably identify it as a gamification-managed gift.
     *
     * @param {string} variantId - Shopify variant id to add.
     * @param {number} giftThreshold - Threshold in cents that justified the gift.
     * @returns {Promise<void>}
     */
    async addGiftToCart(variantId, giftThreshold) {
      gamificationGiftAddInProgress = true;
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId),
              quantity: 1,
              properties: {
                _gift_source: "gamification_bar",
                _gift_threshold: `${giftThreshold}`,
                _gift_variant_id: `${variantId}`
              }
            }],
            sections: sectionsToBundle,
            sections_url: window.location.pathname
          }),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          }
        });
        const responseJson = await response.json();

        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

        if (!response.ok) {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent
          }
        }));
      } finally {
        gamificationGiftAddInProgress = false;
      }
    }

    /**
     * Remove a managed gift line from the cart by line key.
     *
     * @param {string} lineKey
     * @returns {Promise<void>}
     */
    async removeGiftFromCart(lineKey) {
      gamificationGiftRemoveInProgress = true;
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      try {
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: sectionsToBundle.join(",")
          })
        });

        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

        if (!response.ok) {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        const cartContent = await response.json();

        if (window.themeVariables.settings.pageType === "cart") {
          window.location.reload();
          return;
        }

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "line-item:change",
            cart: cartContent
          }
        }));
      } finally {
        gamificationGiftRemoveInProgress = false;
      }
    }

    /**
     * React to global cart change events dispatched by the theme.
     *
     * @param {CustomEvent} event
     * @returns {Promise<void>}
     */
    async onCartChanged(event) {
      const cart = event.detail.cart;
      const previouslyHadManagedGift = this.state.hasManagedGift;

      this.state.totalPrice = cart.total_price || 0;

      if (await this.maybeRemoveStaleGift(cart)) {
        return;
      }

      this.syncAutoGiftDismissState(cart, previouslyHadManagedGift);
      await this.syncManagedGiftState(cart);
      await this.maybeRemoveGift(cart);
      await this.maybeAutoAddGift(cart);
    }

    /**
     * Re-run the same sync flow after the cart drawer DOM is replaced by the theme.
     *
     * @returns {Promise<void>}
     */
    async onDrawerRefreshed() {
      const cart = await fetchCart;
      const previouslyHadManagedGift = this.state.hasManagedGift;

      this.state.totalPrice = cart.total_price || 0;

      if (await this.maybeRemoveStaleGift(cart)) {
        return;
      }

      this.syncAutoGiftDismissState(cart, previouslyHadManagedGift);
      await this.syncManagedGiftState(cart);
      this.updateGamificationBar();
      this.syncGiftChooser();
      await this.maybeRemoveGift(cart);
      await this.maybeAutoAddGift(cart);
    }

    /**
     * React to variant option changes or product radio changes inside the chooser modal.
     *
     * @param {Event} event
     * @returns {void}
     */
    onGiftChoiceChanged(event) {
      if (!event.target.matches("input[data-option-position], [data-gift-product-radio]")) {
        return;
      }

      const giftChoiceItem = event.target.closest("[data-gift-choice-item]");

      if (!giftChoiceItem || giftChoiceItem.getAttribute("data-gift-modal-id") !== this.config.modal_id) {
        return;
      }

      this.syncGiftChooser();
    }

    /**
     * Allow clicking anywhere on a gift card to select that product.
     *
     * @param {Event} event
     * @returns {void}
     */
    onGiftChoiceClicked(event) {
      const giftChoiceItem = event.target.closest("[data-gift-choice-item]");

      if (!giftChoiceItem || giftChoiceItem.getAttribute("data-gift-modal-id") !== this.config.modal_id) {
        return;
      }

      const radioInput = giftChoiceItem.querySelector("[data-gift-product-radio]");

      if (!radioInput || radioInput.checked) {
        return;
      }

      radioInput.checked = true;
      this.syncGiftChooser();
    }

    /**
     * Block manual or DOM-forced submits when a second gift should not be added.
     *
     * This protects the shared chooser form even if someone opens the modal manually from the DOM.
     *
     * @param {SubmitEvent} event
     * @returns {void}
     */
    onGiftSubmit(event) {
      const giftSubmitForm = event.target.closest("[data-gift-submit-form]");

      if (!giftSubmitForm || giftSubmitForm.getAttribute("data-gift-modal-id") !== this.config.modal_id) {
        return;
      }

      if (this.state.hasManagedGift || this.state.totalPrice < this.config.thresholds.gift) {
        event.preventDefault();
        event.stopPropagation();
        this.syncGiftChooser();
      }
    }

    /**
     * Close the chooser modal after a successful gift add from the shared submit form.
     *
     * @param {CustomEvent} event
     * @returns {void}
     */
    onGiftVariantAdded(event) {
      const giftSubmitForm = event.target.closest("[data-gift-submit-form]");

      if (!giftSubmitForm || giftSubmitForm.getAttribute("data-gift-modal-id") !== this.config.modal_id) {
        return;
      }

      this.getGiftModal()?.hide();
    }
  }

  if (!window.customElements.get("gamification-bar")) {
    window.customElements.define("gamification-bar", GamificationBar);
  }
})();